import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiClock, FiCrosshair, FiRefreshCw, FiTrendingUp, FiUsers, FiVideo } from 'react-icons/fi';
import { API_BASE_URL } from '../config';
// Types, the task vocabulary and the description search moved to ./whShared when
// the Datasets tab landed — both tabs read the same /videos payload and offer the
// same tasks, so defining them twice would let them drift.
import {
  SEARCH_HIT_LIMIT,
  SearchHit,
  TOP_ACTIVITIES,
  VideosResponse,
  WHEvent,
  WHVideo,
  runDescriptionSearch,
} from './whShared';

import './WearerHandsPage.css';

// File System Access API — not in this TS lib version's DOM types.
declare global {
  interface Window {
    showDirectoryPicker?: (opts?: {
      id?: string;
      mode?: 'read' | 'readwrite';
      startIn?: 'downloads' | 'documents' | 'desktop' | 'music' | 'pictures' | 'videos';
    }) => Promise<FileSystemDirectoryHandle>;
  }
  interface FileSystemDirectoryHandle {
    getFileHandle(
      name: string,
      opts?: { create?: boolean },
    ): Promise<FileSystemFileHandle>;
    getDirectoryHandle(
      name: string,
      opts?: { create?: boolean },
    ): Promise<FileSystemDirectoryHandle>;
    queryPermission(opts?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
    requestPermission(opts?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
  }
  interface FileSystemFileHandle {
    getFile(): Promise<File>;
    createWritable(): Promise<FileSystemWritableFileStream>;
  }
  interface FileSystemWritableFileStream {
    write(data: BufferSource | Blob | string): Promise<void>;
    close(): Promise<void>;
    abort(): Promise<void>;
  }
}

// ---------- types ----------

interface FleetResponse {
  generatedAt: string;
  shiftWindowDays: number;
  // canonical org name (suffix-split orgs collapsed) -> shift-device counts
  orgs: { [canonical: string]: { total: number; on1080p: number } };
  orgIdToCanonical: { [orgId: string]: string };
}

// Unchecked by default in the "Add task" / "Add industry" menus. The menus read
// as include-lists, but the state underneath stays an EXCLUDE-list on purpose:
// an industry or activity nobody has seen yet is then kept rather than silently
// hidden, which an include-list of names could never guarantee.
const DEFAULT_OFF_CATS = ['Using a phone', 'Driving'];
const DEFAULT_OFF_INDS = ['Private Security', 'Internal-Plix'];

// Above this, the in-memory fallback path (no File System Access API) is a bad
// idea — the whole zip would sit in a Blob in the tab.
const BLOB_FALLBACK_WARN_BYTES = 2e9;
// Folder mode asks for clips in batches so the SERVER does the parallel cutting.
// One request per clip would cap concurrency at the browser's 6 connections per
// host and leave the cutter pool idle; a batch of 8 across 2 requests keeps all
// 16 cutter threads busy, matching zip-mode throughput.
const CLIP_BATCH = 8;
const BATCH_CONCURRENCY = 2;
const CLIP_CONCURRENCY = CLIP_BATCH * BATCH_CONCURRENCY;
// Download size estimate. Clips are stream-copied, so a clip costs exactly the
// source's bitrate: the fleet video config is 1080p @ 2 Mbps, plus ~0.13 Mbps
// AAC. Only ever shown as a "~" figure.
const EST_MBPS = 2.13;
// Measured cost of cutting one clip: ~3.6s, nearly all of it the seek into the
// source blob, so it barely varies with clip length. Wall-clock is that over
// however many cuts run at once — the server's pool in zip mode, the browser's
// 6-connection cap in folder mode.
const EST_CLIP_CUT_S = 3.6;
const SERVER_CLIP_WORKERS = 16;

// The picked download directory, remembered across sessions. A directory handle
// survives in IndexedDB (it is structured-cloneable, unlike localStorage), and
// Chrome keeps the permission attached to it — so the folder is chosen once and
// every later download goes straight there. There is no API for writing to disk
// without that first selection.
const DIR_DB = 'wh-download';
const DIR_STORE = 'handles';
const DIR_KEY = 'clipsDir';

const dirDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DIR_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(DIR_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const loadSavedDir = async (): Promise<FileSystemDirectoryHandle | null> => {
  try {
    const db = await dirDb();
    return await new Promise((resolve) => {
      const req = db.transaction(DIR_STORE).objectStore(DIR_STORE).get(DIR_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

const saveDir = async (handle: FileSystemDirectoryHandle) => {
  try {
    const db = await dirDb();
    db.transaction(DIR_STORE, 'readwrite').objectStore(DIR_STORE).put(handle, DIR_KEY);
  } catch {
    /* remembering the folder is a convenience, never a hard failure */
  }
};

// 5% steps, 5..100. Fine enough to hit an hours target: each step moves the
// footage total by roughly 5-12%, where the original 25->50 stop moved it 43%.
const DENSITY_STOPS = Array.from({ length: 20 }, (_, i) => (i + 1) * 5);
const GOAL_STOPS = [10, 50, 100, 500, 1000]; // collection-goal hours
const RATE_DAYS = 7; // trailing complete days used for the collection rate
// Minimum clip length. This is the lever on clip COUNT: measured at density 75%,
// 63% of clips are under 30s but carry only 22% of the footage, so the 30s stop
// throws away most of the files and little of the material.
const LENGTH_STOPS = [0, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300];

// Floor on how much hand evidence a clip has to rest on, in SAMPLED SECONDS.
// Fixed, not a dial: there is one right answer here and it is cheap.
//
// The density dial cannot do this job. A 36-second window whose whole evidence
// is one or two sampled hand-seconds and no negatives scores 100% density, so
// raising that dial CONCENTRATES these rather than removing them (measured over
// the rebuilt store: clips on <=2 hand-seconds are 4.5% of the set at density
// 10%, 5.3% at 40%, 9.2% at 75%).
//
// 3 costs 2,526 clips and 25 h out of 974 h -- 2.6% of footage -- and those
// clips have a median span of 36 s against a median density of 100%, which is
// the signature of the failure mode. The next stop up (4) costs 87 h for clips
// that are no longer obviously junk, so this is the whole of the cheap win.
export const MIN_HAND_SECONDS = 3;


// ---------- helpers ----------

// The one place clips are derived from server events: density picks which events
// survive, length drops the ones too short to be worth a file. Player chips, the
// download, the estimate, the CSV and the projection all come through here, so
// they cannot disagree about what a clip is.
//
// There is deliberately no merge knob. The server already bridges every gap
// under 120s while coalescing, and with the 5s padding on each side that puts a
// hard floor of 111s on the gap between any two clips -- measured identical at
// every density stop. Nothing is left to merge that wouldn't be swallowing
// minutes of hands-free footage.
// Ingest's coalescer pads every detection by this much (WH_COALESCE_RADIUS): at
// ~1 fps sampling a detection is evidence of activity *around* it, not only *at*
// it. Mirrored here so a client-side re-split lands where the server would have.
const COALESCE_RADIUS = 5;

const countIn = (xs: number[], s: number, e: number) =>
  xs.reduce((n, x) => (x >= s && x <= e ? n + 1 : n), 0);

// Cut an event wherever it runs longer than `maxGapS` with no hand detection.
// This is the buyer's "hands out of frame for 10s or more -> remove the clip"
// rule, enforced at export time rather than baked into ingest.
//
// Split-only, never merge: the server bridges gaps up to WH_BRIDGE_MAX (120s),
// so every value this slider offers is a refinement of what came back.
function splitByGap(
  ev: WHEvent,
  hs: number[],
  negs: number[],
  maxGapS: number,
  duration: number,
): WHEvent[] {
  const inside = hs.filter((x) => x >= ev.start && x <= ev.end);
  if (inside.length < 2) return [ev];

  const runs: Array<[number, number]> = [];
  let a = inside[0];
  let b = inside[0];
  for (const x of inside.slice(1)) {
    if (x - b <= maxGapS) {
      b = x;
    } else {
      runs.push([a, b]);
      a = x;
      b = x;
    }
  }
  runs.push([a, b]);
  if (runs.length === 1) return [ev]; // nothing to cut; keep the server's padding

  const hi = Math.max(0, Math.round(duration) - 1);
  return runs.map(([s0, e0], i) => {
    // Never pad past the midpoint of the gap just cut. Padding the full radius
    // on both sides of a short gap would glue the halves back together and
    // re-create the exact stretch this slider exists to remove.
    const padL =
      i === 0
        ? COALESCE_RADIUS
        : Math.min(COALESCE_RADIUS, Math.floor((s0 - runs[i - 1][1] - 1) / 2));
    const padR =
      i === runs.length - 1
        ? COALESCE_RADIUS
        : Math.min(COALESCE_RADIUS, Math.floor((runs[i + 1][0] - e0 - 1) / 2));
    const s = Math.max(ev.start, s0 - padL);
    const e = Math.min(hi, ev.end, e0 + padR);
    const nh = countIn(hs, s, e);
    const nn = countIn(negs, s, e);
    return {
      start: s,
      end: e,
      // Per-second scores are not in the payload, so a child inherits its
      // parent's peak. Only used for display ordering, never for filtering.
      score: ev.score,
      hands: nh,
      seenDensity: nh + nn ? nh / (nh + nn) : 0,
    };
  });
}

export function buildWindows(
  video: WHVideo,
  densityPct: number,
  minClipS: number,
  maxGapS: number,
): WHEvent[] {
  const split = video.events.flatMap((e) =>
    splitByGap(e, video.handSeconds, video.negSeconds || [], maxGapS, video.duration),
  );
  // Two independent questions, in order: were hands a MAJORITY of what we
  // looked at (density), and did we look at enough of them for that ratio to
  // mean anything (MIN_HAND_SECONDS). A clip has to clear both.
  const kept = split.filter(
    (e) => e.seenDensity * 100 >= densityPct && e.hands >= MIN_HAND_SECONDS,
  );
  return minClipS > 0
    ? kept.filter((w) => w.end - w.start + 1 >= minClipS)
    : kept;
}

// Hand-seconds INSIDE the clips being kept -- not every hand-second in the
// video. Hand Time used to read `video.handSeconds.length`, which left ~30% of
// its own total outside the footage Recording Time measures: the two tiles
// answered different questions, so the ratio a reader forms between them
// described neither. Pair this with buildWindows and both move with the dial.
export const handSecondsInWindows = (ws: WHEvent[]) =>
  ws.reduce((a, w) => a + w.hands, 0);

const fmtHours = (s: number) => (s / 3600).toFixed(2);
const fmtHM = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
// "plixXXXX-shift-20260707_093601[-joined].mp4" -> "Jul 7, 9:36 AM" (device-local time)
const recDate = (filename: string) => {
  const m = filename.match(/-(\d{8})_(\d{6})/);
  if (!m) return filename;
  const [, d, t] = m;
  const dt = new Date(
    +d.slice(0, 4), +d.slice(4, 6) - 1, +d.slice(6, 8),
    +t.slice(0, 2), +t.slice(2, 4), +t.slice(4, 6),
  );
  return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtTime = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};
// Lengths in the video table: "12m04s". Minutes never roll into hours here so the
// column stays one unit wide and comparable down the page.
const fmtMS = (s: number) =>
  `${Math.floor(s / 60)}m${String(Math.round(s % 60)).padStart(2, '0')}s`;

// Columns of the per-org video table. `num` right-aligns and tabular-figures the
// cell; `sort` is what the header sorts on.
const VID_COLS: {
  key: string;
  label: string;
  num?: boolean;
}[] = [
  { key: 'recorded', label: 'Recorded' },
  { key: 'length', label: 'Length', num: true },
  { key: 'hands', label: 'Hands', num: true },
  { key: 'clips', label: 'Clips', num: true },
  { key: 'density', label: 'Density', num: true },
  { key: 'task', label: 'Task' },
];

// ---------- player ----------

interface PlayerState {
  video: WHVideo;
  url: string;
  startAt: number;
  hits: SearchHit[];
}

interface DescState {
  // False once the describe pass produced anything usable for this video: a
  // human summary may only fill a gap, never argue with a model description.
  eligible: boolean;
  gatedCount: number;
  model: { frameId: number; description: string; gated: boolean }[];
  human: { frameId: number; description: string } | null;
  canWrite: boolean;
}

// Write-in summary for videos the describe pass gave up on. It goes to
// descriptionsv1 embedded in the same MiniLM space as the model's own
// descriptions, so it is searchable from the same box — tagged human-labeler,
// and re-embedded on every edit so the vector can never drift from the text.
const SummaryBox: React.FC<{
  video: WHVideo;
  onSaved: () => void;
  onJump: (t: number) => void;
}> = ({ video, onSaved, onJump }) => {
  const [st, setSt] = useState<DescState | null>(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setSt(null);
    setErr(null);
    setEditing(false);
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/wearer-hands/description?filename=${encodeURIComponent(video.filename)}`,
      );
      if (!r.ok) throw new Error(`load failed (${r.status})`);
      const j: DescState = await r.json();
      setSt(j);
      setDraft(j.human?.description || '');
    } catch (e: any) {
      setErr(e.message);
    }
  }, [video.filename]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: video.filename,
          text,
          // Only used when the video has no description docs at all; the first
          // hand event is the offset a search hit should land on.
          frameId: video.events[0]?.start ?? 0,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `save failed (${r.status})`);
      await load();
      onSaved(); // the Task column and "has a task" both key off this
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (err && !st) return <div className="wh-summary wh-summary-err">{err}</div>;
  if (!st) return <div className="wh-summary wh-summary-muted">loading summary…</div>;

  if (!st.eligible) {
    // Already described, so there is nothing to write — but the description is
    // the point of the box, not the fact that one exists. One per hand event,
    // stamped so it can be found in the video.
    const said = st.model.filter((m) => !m.gated);
    return (
      <div className="wh-summary">
        <div className="wh-summary-head">
          <strong>Summary</strong>
        </div>
        <div className="wh-summary-list">
          {said.map((m) => (
            <div key={m.frameId}>
              “{m.description}”{' '}
              <button type="button" className="wh-summary-jump" onClick={() => onJump(m.frameId)}>
                {fmtTime(m.frameId)}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const show = editing || !st.human;
  return (
    <div className="wh-summary">
      <div className="wh-summary-head">
        <strong>Summary</strong>
        <span className="wh-summary-note">
          {st.human ? (
            <>human labeled</>
          ) : (
            <>
              the model found nothing here
              {st.gatedCount > 0 ? ` (${st.gatedCount} gated)` : ''} — describe it yourself
            </>
          )}
        </span>
      </div>
      {show ? (
        <>
          <textarea
            value={draft}
            maxLength={1000}
            placeholder="What are the wearer's hands doing? e.g. connecting a trailer to a tow hitch"
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving || !st.canWrite}
          />
          <div className="wh-summary-actions">
            <button type="button" onClick={save} disabled={saving || !draft.trim() || !st.canWrite}>
              {saving ? 'Saving…' : st.human ? 'Replace summary' : 'Save summary'}
            </button>
            {st.human && (
              <button type="button" onClick={() => { setEditing(false); setDraft(st.human!.description); }}>
                Cancel
              </button>
            )}
            {!st.canWrite && (
              <span className="wh-summary-note">no embedding service configured</span>
            )}
            {err && <span className="wh-summary-err">{err}</span>}
          </div>
        </>
      ) : (
        <div className="wh-summary-body">
          “{st.human!.description}”
          <button type="button" className="wh-summary-edit" onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

const InlinePlayer: React.FC<{
  state: PlayerState;
  onClose: () => void;
  onSaved: () => void;
}> = ({ state, onClose, onSaved }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Bring the player into view when it opens or switches videos (the page
  // scrolls inside the app's content area, so window.scrollTo doesn't cut it).
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [state.video.filename]);

  const jump = (t: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, t);
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div className="wh-player" ref={rootRef}>
      <div className="wh-player-head">
        <div>
          <div className="wh-player-title">{recDate(state.video.filename)}</div>
          <div className="wh-player-sub">{state.video.orgName}</div>
        </div>
        <div className="wh-player-buttons">
          <button className="wh-close" onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </div>
      <video
        ref={videoRef}
        controls
        autoPlay
        preload="metadata"
        src={`${state.url}#t=${state.startAt}`}
      />
      <SummaryBox video={state.video} onSaved={onSaved} onJump={jump} />
      {state.hits.length > 0 && (
        <div className="wh-player-desc">
          {state.hits.map((h, i) => (
            <div key={i}>
              “{h.description}”{' '}
              <em>
                {fmtTime(h.timestamp)} · {h.similarity.toFixed(2)}
              </em>
            </div>
          ))}
        </div>
      )}
      <div className="wh-jumps">
        <span className="wh-jumps-label">Hand events:</span>
        {state.video.events.map((e, i) => (
          <button key={i} className="wh-chip" onClick={() => jump(e.start)}>
            {fmtTime(e.start)}
            {e.end > e.start ? `–${fmtTime(e.end)}` : ''}
          </button>
        ))}
        {state.hits.length > 0 && <span className="wh-jumps-label">Matches:</span>}
        {state.hits.map((h, i) => (
          <button
            key={`m${i}`}
            className="wh-chip wh-chip-match"
            title={h.description}
            onClick={() => jump(Math.max(0, h.timestamp - 3))}
          >
            {fmtTime(h.timestamp)} <em>({h.similarity.toFixed(2)})</em>
          </button>
        ))}
      </div>
    </div>
  );
};

// ---------- page ----------

const WearerHandsPage: React.FC = () => {
  const [hours, setHours] = useState(0); // 0 = all time
  // 40%, not the old 10%. Measured over the whole store (rebuilt 2026-08-25,
  // after the MIN_RUN negatives fix): 10% removed 0 clips of 56,299 -- the dial
  // was inert at its own default. 40% keeps 84.7% of clips and 1,027h of 1,165h,
  // which is the first stop where it does real work without cutting into any
  // plausible delivery target.
  //
  // What this does NOT do is drop thin clips, and it is worth not expecting it
  // to: a window with 2 hand-seconds and 0 negatives scores 100%, so raising
  // this dial CONCENTRATES them (4.5% of clips rest on <=2 hand-seconds at 10%,
  // 5.3% at 40%, 9.2% at 75%). Density only ever answers "were hands a minority
  // of the seconds we looked at" -- clip thinness needs its own floor.
  const [densityPct, setDensityPct] = useState(40);
  // 30s is the floor the data buyer specified ("minimum clip size is 30 seconds"),
  // so it is the default rather than an opt-in.
  const [minClipS, setMinClipS] = useState(30);
  // Buyer rule: "if the hands are out of frame for 10 seconds or more, remove the
  // clip". The top of the range is 60s -- still a real cut, because ingest
  // bridges unsampled gaps up to WH_BRIDGE_MAX (120s), not just the 30s it
  // joins unconditionally.
  //
  // 30s, not the old 20s. Measured over the whole store at the other defaults:
  // 20s -> 49,814 clips / 870h, 30s -> 56,600 clips / 1,176h. The count rises
  // with the gap because a looser cut makes each child LONGER, so fewer of them
  // fall under the 30s minimum length and get dropped. The cost is real and is
  // the reason this is a knob: median wall-clock hand density falls 22.5% ->
  // 18.6%, and clips resting on two sampled hand-seconds go 0.9% -> 4.5%.
  const [maxGapS, setMaxGapS] = useState(30);
  // Keep only videos with a known task. The activity hit sets are what "known"
  // means, so this is dead until they finish warming.
  const [taskOnly, setTaskOnly] = useState(false);
  // What descriptionsv1 knows about each video: hand-written summaries, and the
  // set the model already described (which is therefore closed to labelling).
  // Re-read whenever a summary is saved, "has a task" is ticked, or a download
  // starts — all three depend on it being current.
  const [descState, setDescState] = useState<{
    labels: Map<string, string>;
    human: Map<string, string>;
  } | null>(null);
  const humanLabels = descState?.human;
  const [data, setData] = useState<VideosResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [queryText, setQueryText] = useState('');
  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [searchHits, setSearchHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Both hold what is NOT selected — see DEFAULT_OFF_CATS.
  const [excludedCats, setExcludedCats] = useState<string[]>(DEFAULT_OFF_CATS);
  const [excludedInds, setExcludedInds] = useState<string[]>(DEFAULT_OFF_INDS);
  // Per-query search results (SEARCH_HIT_LIMIT), fetched once in the background.
  // An activity group's hit set is the union over its queries. Powers BOTH
  // chip availability (only show activities that return results in the
  // current view) and the Remove dropdown's exclusion sets.
  const [activityHits, setActivityHits] = useState<{ [query: string]: SearchHit[] }>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [clipsOpen, setClipsOpen] = useState(false);
  // Accordion, both shut on open: 27 task rows would otherwise bury Industry
  // below the fold every time.
  const [openSection, setOpenSection] = useState<'task' | 'industry' | null>(null);

  const [player, setPlayer] = useState<PlayerState | null>(null);
  const [cardPage, setCardPage] = useState(0);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [vidSort, setVidSort] = useState<{ key: string; desc: boolean }>({
    key: 'recorded',
    desc: true,
  });
  const [hideNight, setHideNight] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [dlProgress, setDlProgress] = useState<{
    bytes: number;
    clips: number | null; // clips finished (folder mode)
    total: number | null;
    skipped: number;
    failed: number;
    pct: number;
    etaS: number | null;
  } | null>(null);
  const folderMode = typeof window.showDirectoryPicker === 'function';
  const [savedDirName, setSavedDirName] = useState<string | null>(null);
  const [dlOpen, setDlOpen] = useState(false);
  const [dlStage, setDlStage] = useState<
    'confirm' | 'running' | 'done' | 'cancelled' | 'error'
  >('confirm');
  const dlAbort = useRef<AbortController | null>(null);

  // 1080p-config adoption per org (loads in the background; '–' until ready).
  const [fleet, setFleet] = useState<FleetResponse | null>(null);
  // All-time dataset for the collection-rate projection — independent of the
  // selected window so the rate doesn't change when you narrow the view.
  const [allData, setAllData] = useState<VideosResponse | null>(null);
  const [goalHours, setGoalHours] = useState(100);

  // Surface the remembered download folder when the dialog opens, so the
  // destination is visible before committing rather than a surprise.
  useEffect(() => {
    if (!dlOpen || !folderMode) return;
    loadSavedDir().then(async (h) => {
      if (!h) return setSavedDirName(null);
      const state = await h.queryPermission({ mode: 'readwrite' }).catch(() => 'denied');
      setSavedDirName(state === 'denied' ? null : h.name);
    });
  }, [dlOpen, folderMode]);

  const isNight = useCallback(
    (v: WHVideo) =>
      v.nightMedian !== null &&
      data?.nightThreshold != null &&
      v.nightMedian >= data.nightThreshold,
    [data],
  );

  const loadVideos = useCallback(async (h: number, refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Only originals recorded at >=28fps (low-fps sources get transcoded up
      // and remain visually imperfect — not curation material).
      const resp = await fetch(
        `${API_BASE_URL}/api/wearer-hands/videos?hours=${h}&min_fps=28${refresh ? '&refresh=1' : ''}`,
      );
      if (!resp.ok) throw new Error(`videos fetch failed (${resp.status})`);
      const json = await resp.json();
      setData(json);
      // All-time view is the projection's dataset too — no second fetch needed.
      if (h === 0) setAllData(json);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos(hours);
  }, [hours, loadVideos]);

  // Background loads, once per page visit. Both fail soft (the tiles/column
  // they feed just stay in their loading state).
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/wearer-hands/device1080p`);
        if (r.ok) setFleet(await r.json());
      } catch {}
    })();
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/wearer-hands/videos?hours=0&min_fps=28`);
        if (r.ok) setAllData(await r.json());
      } catch {}
    })();
  }, []);

  // Refresh button: bust the server response cache for the view (and the
  // all-time projection dataset when it's a separate window), and re-pull the
  // fleet stats (its own server-side cache decides whether to recompute).
  const doRefresh = () => {
    loadVideos(hours, true);
    if (hours !== 0) {
      (async () => {
        try {
          const r = await fetch(`${API_BASE_URL}/api/wearer-hands/videos?hours=0&min_fps=28&refresh=1`);
          if (r.ok) setAllData(await r.json());
        } catch {}
      })();
    }
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/wearer-hands/device1080p`);
        if (r.ok) setFleet(await r.json());
      } catch {}
    })();
  };

  const doSearch = async (q: string) => {
    const query = q.trim();
    if (!query) {
      setActiveQuery(null);
      setSearchHits(null);
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const hits = await runDescriptionSearch(query, 20);
      setActiveQuery(query);
      setSearchHits(hits);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const fetchActivityHits = useCallback(async (query: string) => {
    try {
      // High cap, not a page size: these hits ARE the activity tally, so a limit
      // the result set can reach silently under-counts the chip.
      // NOT slim and NOT the 12000 tally ceiling: the player quotes h.description
      // for every match, so this payload has to carry the prose. It is therefore
      // still truncated on the three busiest queries -- see SEARCH_HIT_LIMIT.
      const hits = await runDescriptionSearch(query, SEARCH_HIT_LIMIT);
      setActivityHits((prev) => (query in prev ? prev : { ...prev, [query]: hits }));
    } catch {
      setActivityHits((prev) => (query in prev ? prev : { ...prev, [query]: [] }));
    }
  }, []);

  // Union of a group's per-query hits, best-similarity first, exact dupes
  // (same filename+timestamp reached by two phrasings) collapsed.
  const groupHits = useCallback(
    (a: { queries: string[] }) => {
      const seen = new Set<string>();
      const merged: SearchHit[] = [];
      a.queries
        .flatMap((q) => activityHits[q] || [])
        .sort((x, y) => y.similarity - x.similarity)
        .forEach((h) => {
          const k = `${h.filename}#${h.timestamp}`;
          if (!seen.has(k)) {
            seen.add(k);
            merged.push(h);
          }
        });
      return merged;
    },
    [activityHits],
  );

  // Warm all activity result sets once (a few at a time; server embeds serially).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const queue = TOP_ACTIVITIES.flatMap((a) => a.queries);
      const workers = Array.from({ length: 3 }, async () => {
        while (queue.length && !cancelled) {
          await fetchActivityHits(queue.shift()!);
        }
      });
      await Promise.all(workers);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchActivityHits]);

  const loadDescState = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/descriptions/state`);
      if (r.ok) {
        const j = await r.json();
        setDescState({
          labels: new Map(Object.entries(j.labels || {}) as [string, string][]),
          human: new Map(Object.entries(j.human || {}) as [string, string][]),
        });
      }
    } catch {
      /* the task column falls back to activity hits alone */
    }
  }, []);

  useEffect(() => {
    loadDescState();
  }, [loadDescState]);

  const toggleExcluded = (label: string) => {
    setExcludedCats((prev) =>
      prev.includes(label) ? prev.filter((c) => c !== label) : [...prev, label],
    );
  };

  const includeAllCats = () => setExcludedCats([]);
  const excludeAllCats = () => setExcludedCats(TOP_ACTIVITIES.map((a) => a.label));

  const toggleExcludedInd = (name: string) => {
    setExcludedInds((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name],
    );
  };

  // ---------- filtering + stats (all client-side) ----------

  // Industries present in the loaded window, busiest first, so the menu only
  // offers what's actually removable right now.
  const industryRows = useMemo(() => {
    const n = new Map<string, number>();
    (data?.videos || []).forEach((v) => n.set(v.industry, (n.get(v.industry) || 0) + 1));
    return Array.from(n.entries()).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const includeAllInds = () => setExcludedInds([]);
  const excludeAllInds = () => setExcludedInds(industryRows.map(([name]) => name));
  // Badge on the Filter button: how much is being removed right now. Industries
  // are counted only where they exist in the window, so a default that names an
  // absent industry doesn't inflate it.
  const nCatsRemoved = excludedCats.length + (hideNight ? 1 : 0);
  const nIndsRemoved = industryRows.filter(([name]) => excludedInds.includes(name)).length;
  // "Has a task" counts too — it lives in this menu now, and a filter that is on
  // but invisible behind a closed menu is how you lose an afternoon.
  const nRemoved = nCatsRemoved + nIndsRemoved + (taskOnly ? 1 : 0);

  // Every search hit we hold, keyed by video and tagged with what it matched.
  // Activity hits are warmed for the whole top set at load, so this is populated
  // even when nothing is filtered; a free-text search has no activity label, so
  // its hits carry the query itself. Feeds both the "has a task" filter and the
  // task column of the download manifest.
  const labelledHits = useMemo(() => {
    const m = new Map<string, { t: number; label: string; sim: number }[]>();
    const push = (fn: string, t: number, label: string, sim: number) => {
      const arr = m.get(fn);
      if (arr) arr.push({ t, label, sim });
      else m.set(fn, [{ t, label, sim }]);
    };
    TOP_ACTIVITIES.forEach((a) =>
      a.queries.forEach((q) =>
        (activityHits[q] || []).forEach((h) =>
          push(h.filename, h.timestamp, a.label, h.similarity),
        ),
      ),
    );
    if (activeQuery && !TOP_ACTIVITIES.some((a) => a.label === activeQuery)) {
      (searchHits || []).forEach((h) =>
        push(h.filename, h.timestamp, activeQuery, h.similarity),
      );
    }
    return m;
  }, [activityHits, activeQuery, searchHits]);

  // The task filter is only meaningful once every activity query has answered —
  // before that it would silently hide videos whose task simply hasn't loaded.
  const hitsWarmed =
    Object.keys(activityHits).length === TOP_ACTIVITIES.flatMap((a) => a.queries).length;

  const excludedFilenames = useMemo(() => {
    const s = new Set<string>();
    excludedCats.forEach((label) => {
      const a = TOP_ACTIVITIES.find((x) => x.label === label);
      a?.queries.forEach((q) => (activityHits[q] || []).forEach((h) => s.add(h.filename)));
    });
    return s;
  }, [excludedCats, activityHits]);

  // Task removal keys off search-hit filenames; industry removal keys off the
  // video's own org. Both funnel through here so the activity cards, the video
  // list and the collection projection can never disagree about what's in view.
  const excludedIndSet = useMemo(() => new Set(excludedInds), [excludedInds]);
  const isRemoved = useCallback(
    (v: WHVideo) => excludedFilenames.has(v.filename) || excludedIndSet.has(v.industry),
    [excludedFilenames, excludedIndSet],
  );

  // One card per activity with results under the current filters: distinct
  // in-view videos, top orgs by video count, and the best-similarity hit as
  // the preview frame. Sorted most-common first for the paged strip.
  const activityCards = useMemo(() => {
    if (!data) return [];
    const byName = new Map(data.videos.map((v) => [v.filename, v]));
    return TOP_ACTIVITIES.map((a) => {
      const hits = groupHits(a).filter((h) => {
        const v = byName.get(h.filename);
        return !!v && !isRemoved(v) && !(hideNight && isNight(v));
      });
      const perOrg = new Map<string, number>();
      const seen = new Set<string>();
      hits.forEach((h) => {
        if (seen.has(h.filename)) return;
        seen.add(h.filename);
        const org = byName.get(h.filename)!.orgName;
        perOrg.set(org, (perOrg.get(org) || 0) + 1);
      });
      const orgs = Array.from(perOrg.entries())
        .sort((x, y) => y[1] - x[1])
        .slice(0, 3)
        .map(([o]) => o);
      return { activity: a, count: seen.size, orgs, preview: hits[0] || null };
    })
      .filter((c) => c.count > 0)
      .sort(
        (x, y) =>
          (x.activity.tier ?? 1) - (y.activity.tier ?? 1) || y.count - x.count,
      );
  }, [data, groupHits, isRemoved, hideNight, isNight]);

  const CARDS_PER_PAGE = 5;
  const maxCardPage = Math.max(0, Math.ceil(activityCards.length / CARDS_PER_PAGE) - 1);
  const page = Math.min(cardPage, maxCardPage);
  const pageCards = activityCards.slice(page * CARDS_PER_PAGE, (page + 1) * CARDS_PER_PAGE);

  const view = useMemo(() => {
    if (!data) return null;
    const byName = new Map(data.videos.map((v) => [v.filename, v]));
    let vids: { video: WHVideo; hit?: SearchHit }[];
    let droppedBySearch = 0;
    if (searchHits) {
      const seen = new Set<string>();
      vids = [];
      for (const h of searchHits) {
        const v = byName.get(h.filename);
        if (!v) {
          droppedBySearch += 1;
          continue;
        }
        if (seen.has(h.filename)) continue;
        seen.add(h.filename);
        vids.push({ video: v, hit: h });
      }
    } else {
      vids = data.videos.map((v) => ({ video: v }));
    }
    const beforeExcl = vids.length;
    vids = vids.filter(({ video }) => !excludedIndSet.has(video.industry));
    const removedByInds = beforeExcl - vids.length;

    const beforeCats = vids.length;
    vids = vids.filter(({ video }) => !excludedFilenames.has(video.filename));
    const removedByCats = beforeCats - vids.length;

    let nightRemoved = 0;
    if (hideNight) {
      const beforeNight = vids.length;
      vids = vids.filter(({ video }) => !isNight(video));
      nightRemoved = beforeNight - vids.length;
    }

    // Clips ARE the server's hand events — the same grouping the player chips
    // show. They used to be re-derived here from handSeconds, which had no way
    // to tell "never sampled" from "no hands" and so shattered one activity into
    // many fragments (one real video: 3 chips but 9 clips). The dial no longer
    // draws boundaries, it only decides which events are dense enough to keep.
    const windows = new Map<string, WHEvent[]>();
    vids.forEach(({ video }) =>
      windows.set(
        video.filename,
        buildWindows(video, densityPct, minClipS, maxGapS),
      ),
    );
    const beforeSparse = vids.length;
    vids = vids.filter(({ video }) => windows.get(video.filename)!.length > 0);
    const removedBySparse = beforeSparse - vids.length;

    // "Has a known task": at least one activity hit lands inside a clip we are
    // actually keeping. Requiring it inside a clip rather than anywhere in the
    // video is what keeps this honest — a video kept here always produces at
    // least one manifest row with a task on it.
    let removedByNoTask = 0;
    if (taskOnly) {
      const beforeTask = vids.length;
      vids = vids.filter(({ video }) => {
        // A summary someone wrote by hand counts outright — no window test and
        // no similarity floor. The activity queries only recognise 26 phrasings,
        // so matching them is luck of wording, not evidence of a missing task.
        if (humanLabels?.has(video.filename)) return true;
        const hs = labelledHits.get(video.filename);
        if (!hs) return false;
        const ws = windows.get(video.filename)!;
        return hs.some(({ t }) => ws.some((w) => t >= w.start && t <= w.end));
      });
      removedByNoTask = beforeTask - vids.length;
    }

    const winLen = (ws: WHEvent[]) => ws.reduce((a, w) => a + (w.end - w.start + 1), 0);
    const totalHandS = vids.reduce(
      (a, { video }) => a + handSecondsInWindows(windows.get(video.filename)!),
      0,
    );

    const rawS = vids.reduce((a, { video }) => a + video.duration, 0);
    const croppedS = vids.reduce((a, { video }) => a + winLen(windows.get(video.filename)!), 0);

    const perOrg = new Map<
      string,
      {
        vids: { video: WHVideo; hit?: SearchHit }[];
        handS: number;
        croppedS: number;
        dens: number[];
        orgIds: Set<string>;
      }
    >();
    vids.forEach((entry) => {
      const { video } = entry;
      const ws = windows.get(video.filename)!;
      const len = winLen(ws);
      const inWin = handSecondsInWindows(ws);
      const o =
        perOrg.get(video.orgName) ||
        { vids: [], handS: 0, croppedS: 0, dens: [], orgIds: new Set<string>() };
      o.vids.push(entry);
      o.handS += inWin;
      o.croppedS += len;
      o.dens.push(len > 0 ? inWin / len : 0);
      o.orgIds.add(video.orgId);
      perOrg.set(video.orgName, o);
    });

    return {
      vids,
      windows,
      droppedBySearch,
      removedByCats,
      removedByInds,
      removedBySparse,
      removedByNoTask,
      nightRemoved,
      totalHandS,
      rawS,
      croppedS,
      orgRows: Array.from(perOrg.entries()).sort((a, b) => b[1].handS - a[1].handS),
    };
  }, [
    data,
    searchHits,
    excludedFilenames,
    excludedIndSet,
    densityPct,
    minClipS,
    maxGapS,
    taskOnly,
    labelledHits,
    humanLabels,
    hideNight,
    isNight,
  ]);

  // What the Task column says for one video: whatever is known to describe it,
  // with no distinction drawn between a person's summary and the model's — both
  // are a label. Failing that, the activity search's majority label; failing
  // that, it is unlabelled and belongs in the curation queue.
  const taskOf = useCallback(
    (fn: string): { kind: string; text: string; title?: string } => {
      const hits = labelledHits.get(fn);
      if (hits?.length) {
        const n = new Map<string, { c: number; best: number }>();
        hits.forEach(({ label, sim }) => {
          const e = n.get(label);
          if (e) {
            e.c += 1;
            e.best = Math.max(e.best, sim);
          } else n.set(label, { c: 1, best: sim });
        });
        const top = Array.from(n.entries()).sort(
          (a, b) => b[1].c - a[1].c || b[1].best - a[1].best,
        )[0][0];
        return { kind: 'hit', text: top };
      }
      // Described, but by nothing that maps onto one of the 26 activities. The
      // full text is a sentence — too long for a column, so it goes in the
      // tooltip and the cell says what the state is.
      const label = descState?.labels.get(fn);
      if (label) {
        return { kind: 'summarized', text: 'Summarized (not mapped to task)', title: label };
      }
      return {
        kind: 'none',
        text: 'Unlabeled',
        title: 'Nothing describes this video yet — open it to write a summary',
      };
    },
    [descState, labelledHits],
  );

  // Sort order inside an expanded org. Unlabelled videos are NOT floated: the
  // default is plain reverse-chronological, and any column can be clicked.
  const sortVids = useCallback(
    (vids: { video: WHVideo; hit?: SearchHit }[]) => {
      const val = ({ video }: { video: WHVideo }) => {
        switch (vidSort.key) {
          case 'length':
            return video.duration;
          case 'hands':
            return video.handSeconds.length;
          case 'clips':
            return (view?.windows.get(video.filename) || []).length;
          case 'density': {
            const ws = view?.windows.get(video.filename) || [];
            return ws.length ? ws.reduce((a, w) => a + w.seenDensity, 0) / ws.length : 0;
          }
          case 'task':
            return taskOf(video.filename).text.toLowerCase();
          default:
            // The filename's own stamp sorts chronologically as a string.
            return video.filename.match(/-(\d{8}_\d{6})/)?.[1] || video.filename;
        }
      };
      return [...vids].sort((a, b) => {
        const x = val(a);
        const y = val(b);
        const c = typeof x === 'string' ? x.localeCompare(y as string) : (x as number) - (y as number);
        return vidSort.desc ? -c : c;
      });
    },
    [vidSort, view, taskOf],
  );

  // ---------- 1080p adoption + collection projection ----------

  // Shift-device 1080p stats for one org row (rows key by display name; a row
  // may span several orgUserIds, which the fleet map collapses to canonicals).
  const orgFleet = useCallback(
    (orgIds: Set<string>) => {
      if (!fleet) return null;
      const canons = new Set<string>();
      orgIds.forEach((id) => {
        const c = fleet.orgIdToCanonical[id];
        if (c && fleet.orgs[c]) canons.add(c);
      });
      let total = 0;
      let on = 0;
      canons.forEach((c) => {
        total += fleet.orgs[c].total;
        on += fleet.orgs[c].on1080p;
      });
      return total > 0 ? { pct: (on / total) * 100, on, total } : null;
    },
    [fleet],
  );

  // Aggregate across the orgs currently in view (feeds the Organizations tile).
  const fleetSummary = useMemo(() => {
    if (!fleet || !view) return null;
    const all = new Set<string>();
    view.orgRows.forEach(([, o]) => o.orgIds.forEach((id) => all.add(id)));
    return orgFleet(all);
  }, [fleet, view, orgFleet]);

  // Collection rate: density-window hours per day over the last RATE_DAYS
  // complete days (today's partial day excluded), from the all-time dataset
  // with the same night + category filters as the view. The active search is
  // deliberately NOT applied — it's an exploration tool, not a curation filter.
  // Hand tagging has a hard start date, so the averaging window never reaches
  // past the dataset's first recording day (else early zero-days would
  // understate the rate until RATE_DAYS of history exist).
  const projection = useMemo(() => {
    if (!allData) return null;
    const isNightAll = (v: WHVideo) =>
      v.nightMedian !== null &&
      allData.nightThreshold != null &&
      v.nightMedian >= allData.nightThreshold;

    const dayOf = (fn: string) => fn.match(/-(\d{8})_/)?.[1] ?? null;
    const dayKey = (d: Date) =>
      `${d.getFullYear()}` +
      `${String(d.getMonth() + 1).padStart(2, '0')}` +
      `${String(d.getDate()).padStart(2, '0')}`;

    let firstDay: string | null = null;
    allData.videos.forEach((v) => {
      const k = dayOf(v.filename);
      if (k && (!firstDay || k < firstDay)) firstDay = k;
    });
    if (!firstDay) return { perDay: 0, days: 0, collected: 0 };

    // Trailing complete days, newest first, stopping at the dataset's birth.
    const dayHours = new Map<string, number>();
    const now = new Date();
    for (let i = 1; i <= RATE_DAYS; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = dayKey(d);
      if (key < firstDay) break;
      dayHours.set(key, 0);
    }
    const D = Math.max(1, dayHours.size);

    // One pass: `collected` is the all-time density-window total (what counts
    // toward a goal), while dayHours only accumulates the trailing rate window.
    let collected = 0;
    allData.videos.forEach((v) => {
      if (isRemoved(v)) return;
      if (hideNight && isNightAll(v)) return;
      const winS = buildWindows(v, densityPct, minClipS, maxGapS).reduce(
        (a, w) => a + (w.end - w.start + 1),
        0,
      );
      collected += winS / 3600;
      const k = dayOf(v.filename);
      if (!k || !dayHours.has(k)) return;
      dayHours.set(k, dayHours.get(k)! + winS / 3600);
    });
    let total = 0;
    dayHours.forEach((h) => {
      total += h;
    });
    return { perDay: total / D, days: D, collected };
  }, [allData, isRemoved, hideNight, densityPct, minClipS, maxGapS]);

  // What "Download all" would fetch right now, under the current filters and
  // density. Same windows the button posts, so the numbers can't drift from it.
  const dlEstimate = useMemo(() => {
    if (!view || view.vids.length === 0) return null;
    let clips = 0;
    let seconds = 0;
    view.vids.forEach(({ video }) => {
      const ws = view.windows.get(video.filename)!;
      clips += ws.length;
      ws.forEach((w) => (seconds += w.end - w.start + 1));
    });
    return {
      videos: view.vids.length,
      clips,
      seconds,
      bytes: (seconds * EST_MBPS * 1e6) / 8,
      prepS:
        (clips * EST_CLIP_CUT_S) / (folderMode ? CLIP_CONCURRENCY : SERVER_CLIP_WORKERS),
    };
  }, [view, folderMode]);

  const fmtBytes = (b: number) =>
    b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${Math.round(b / 1e6)} MB`;
  const fmtMins = (s: number) =>
    s >= 3600 ? `${(s / 3600).toFixed(1)} h` : `${Math.max(1, Math.round(s / 60))} min`;

  type ClipSpec = { filename: string; windows: number[][] };
  type ClipJob = { filename: string; start: number; end: number; name: string };

  // Single source of truth for the on-disk name: the manifest rows are built from
  // this too, so a row can never point at a file that isn't there.
  const clipName = (filename: string, start: number, end: number) =>
    `${filename.slice(0, -4)}__t${start}-${end}s.mp4`;

  const toJobs = (specs: ClipSpec[]): ClipJob[] =>
    specs.flatMap((s) =>
      s.windows.map(([start, end]) => ({
        filename: s.filename,
        start,
        end,
        name: clipName(s.filename, start, end),
      })),
    );

  // Reads the /clipbatch framing: a JSON header line, then that many raw bytes,
  // repeated. Yields one clip at a time so each can be written and released
  // rather than accumulating the batch in memory.
  async function* readFrames(
    body: ReadableStream<Uint8Array>,
  ): AsyncGenerator<{ name: string; error?: string; data: Uint8Array }> {
    const reader = body.getReader();
    let buf = new Uint8Array(0);
    let eof = false;

    const pull = async () => {
      const { done, value } = await reader.read();
      if (done) {
        eof = true;
        return;
      }
      const next = new Uint8Array(buf.length + value.length);
      next.set(buf);
      next.set(value, buf.length);
      buf = next;
    };

    for (;;) {
      let nl = buf.indexOf(10);
      while (nl < 0 && !eof) {
        await pull();
        nl = buf.indexOf(10);
      }
      if (nl < 0) return; // clean end of stream
      const head = JSON.parse(new TextDecoder().decode(buf.subarray(0, nl)));
      buf = buf.subarray(nl + 1);
      while (buf.length < head.size && !eof) await pull();
      if (buf.length < head.size) throw new Error('truncated clip stream');
      yield { name: head.name, error: head.error, data: buf.subarray(0, head.size) };
      buf = buf.slice(head.size); // copy: drops the consumed backing buffer
    }
  }

  // Folder mode. Clips are requested in batches (the server cuts a whole batch
  // in parallel) and each arrives as its own file written straight into the
  // chosen directory. A cancel or failure leaves every finished clip usable, and
  // re-running skips whatever is already on disk. Progress is exact.
  const downloadToFolder = async (dir: FileSystemDirectoryHandle, jobs: ClipJob[]) => {
    const signal = dlAbort.current!.signal;
    const t0 = Date.now();
    let done = 0;
    let bytes = 0;
    let skipped = 0;
    let failed = 0;
    let nextBatch = 0;

    const batches: ClipJob[][] = [];
    for (let i = 0; i < jobs.length; i += CLIP_BATCH) {
      batches.push(jobs.slice(i, i + CLIP_BATCH));
    }

    const tick = () => {
      const elapsed = (Date.now() - t0) / 1000;
      const fetched = done - skipped;
      setDlProgress({
        bytes,
        clips: done,
        total: jobs.length,
        skipped,
        failed,
        pct: (done / jobs.length) * 100,
        etaS: fetched > 0 ? Math.round((elapsed / fetched) * (jobs.length - done)) : null,
      });
    };

    const worker = async () => {
      for (;;) {
        if (signal.aborted) return;
        const bi = nextBatch++;
        if (bi >= batches.length) return;

        // Resume: a non-empty file of this name means an earlier run got it, so
        // it never even enters the request.
        const todo: ClipJob[] = [];
        for (const job of batches[bi]) {
          const existing = await dir
            .getFileHandle(job.name)
            .then((h) => h.getFile())
            .catch(() => null);
          if (existing && existing.size > 0) {
            skipped += 1;
            bytes += existing.size;
            done += 1;
          } else {
            todo.push(job);
          }
        }
        tick();
        if (!todo.length) continue;

        try {
          const resp = await fetch(`${API_BASE_URL}/api/wearer-hands/clipbatch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              clips: todo.map((j) => ({ filename: j.filename, start: j.start, end: j.end })),
            }),
            signal,
          });
          if (!resp.ok || !resp.body) {
            failed += todo.length;
            done += todo.length;
            tick();
            continue;
          }
          for await (const frame of readFrames(resp.body)) {
            if (frame.error || frame.data.length === 0) {
              failed += 1;
            } else {
              const handle = await dir.getFileHandle(frame.name, { create: true });
              const w = await handle.createWritable();
              await w.write(frame.data);
              await w.close();
              bytes += frame.data.length;
            }
            done += 1;
            tick();
          }
        } catch (e: any) {
          if (e?.name === 'AbortError') return;
          failed += todo.length;
          done += todo.length;
          tick();
        }
      }
    };

    await Promise.all(Array.from({ length: BATCH_CONCURRENCY }, worker));
    if (signal.aborted) throw new DOMException('cancelled', 'AbortError');
    tick();
  };

  // Fallback for browsers without the File System Access API: one streamed zip,
  // buffered in the tab. Fine for a narrowed selection, warned about above 2 GB.
  const downloadAsZip = async (specs: ClipSpec[], zipName: string) => {
    const resp = await fetch(`${API_BASE_URL}/api/wearer-hands/clips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clips: specs }),
      signal: dlAbort.current!.signal,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `download failed (${resp.status})`);
    }
    const blob = await resp.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = zipName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    setDlProgress({
      bytes: blob.size,
      clips: null,
      total: null,
      skipped: 0,
      failed: 0,
      pct: 100,
      etaS: null,
    });
  };

  const saveTextFile = (name: string, text: string) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  };

  // The directory picker has to run on the click itself, before any await, or
  // the user gesture has expired by the time we ask for it.
  const downloadClips = async (
    specs: ClipSpec[],
    csv: string,
    stem: string,
    tag: string,
  ) => {
    let dir: FileSystemDirectoryHandle | null = null;
    if (folderMode) {
      try {
        // Reuse the remembered folder when its permission is still live, so the
        // picker only ever appears on the first download (or after a revoke).
        let parent = await loadSavedDir();
        if (parent) {
          let state = await parent.queryPermission({ mode: 'readwrite' });
          if (state === 'prompt') state = await parent.requestPermission({ mode: 'readwrite' });
          if (state !== 'granted') parent = null;
        }
        if (!parent) {
          parent = await window.showDirectoryPicker!({
            id: 'wh-clips',
            mode: 'readwrite',
            // Chrome refuses system paths outright ("contains system files"),
            // which includes the home directory root — so don't open there.
            startIn: 'downloads',
          });
          await saveDir(parent);
        }
        // Never write loose into the picked directory — 19k mp4s in someone's
        // Downloads is not a download. create:true also reopens the same folder
        // on a repeat run, which is what makes resume work.
        dir = await parent.getDirectoryHandle(stem, { create: true });
      } catch (e: any) {
        if (e?.name === 'AbortError') return; // picker dismissed — not a download
        // Anything else (blocked folder, permission denied) has to land in the
        // dialog; thrown from here it would only be an unhandled rejection.
        setDlStage('error');
        setError(
          e?.name === 'SecurityError' || /system files/i.test(e?.message || '')
            ? "Chrome blocks that folder. Pick a folder inside Downloads rather than Downloads itself — use New Folder in the picker."
            : `Could not open that folder: ${e?.message || e}`,
        );
        return;
      }
    }

    setError(null);
    dlAbort.current = new AbortController();
    setDlStage('running');
    setDownloading(tag);
    const jobs = toJobs(specs);
    setDlProgress({
      bytes: 0,
      clips: 0,
      total: folderMode ? jobs.length : null,
      skipped: 0,
      failed: 0,
      pct: 0,
      etaS: null,
    });
    try {
      if (dir) {
        // Written before the first clip so a cancelled or resumed run still
        // leaves a manifest covering everything that landed.
        const mh = await dir.getFileHandle('manifest.csv', { create: true });
        const mw = await mh.createWritable();
        await mw.write(csv);
        await mw.close();
        await downloadToFolder(dir, jobs);
      } else {
        saveTextFile(`${stem}.csv`, csv);
        await downloadAsZip(specs, `${stem}.zip`);
      }
      setDlStage('done');
    } catch (e: any) {
      // Cancel aborts the in-flight fetches, which surfaces here as an
      // AbortError — not a failure, so it never reaches the page error banner.
      if (e?.name === 'AbortError') {
        setDlStage('cancelled');
      } else {
        setDlStage('error');
        setError(e.message);
      }
    } finally {
      dlAbort.current = null;
      setDownloading(null);
    }
  };

  const cancelDownload = () => dlAbort.current?.abort();

  // Drop the remembered folder so the next Start download asks again.
  const forgetDir = async () => {
    try {
      const db = await dirDb();
      db.transaction(DIR_STORE, 'readwrite').objectStore(DIR_STORE).delete(DIR_KEY);
    } catch {
      /* nothing to forget */
    }
    setSavedDirName(null);
  };

  const closeDlDialog = () => {
    dlAbort.current?.abort();
    setDlOpen(false);
    setDlStage('confirm');
    setDlProgress(null);
  };

  const dayStamp = () => {
    const d = new Date();
    return (
      String(d.getUTCDate()).padStart(2, '0') +
      String(d.getUTCMonth() + 1).padStart(2, '0') +
      d.getUTCFullYear()
    );
  };

  // Clips go in a subfolder of whatever the user picks, never loose in it —
  // the density is part of the name because it decides the clip boundaries, so
  // two pulls at different settings stay separate (and each stays resumable).
  // Every knob that changes clip boundaries is in the name, so two pulls at
  // different settings never land in the same folder — which is what keeps the
  // skip-if-already-there resume from mixing clips cut two different ways.
  const dlFolderName =
    `plixvideos_${dayStamp()}_${densityPct}pct` +
    (minClipS > 0 ? `_min${minClipS}s` : '') +
    `_gap${maxGapS}s` +
    (taskOnly ? '_task' : '');

  const csvCell = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

  // One row per clip file. A clip's task is the best-matching hit that lands
  // inside its window — a window with no hit is real hand activity that no
  // activity query claimed, so the column is left blank rather than guessed at.
  // The video's own task: the label most of its hits agree on, tie-broken by
  // similarity. Clips with no hit of their own inherit it, on the grounds that a
  // shift spraying in one stretch is usually spraying in the next. Checked
  // against the clips whose real label IS known: the majority label matches
  // 249/306 (81%), and 44% of these videos only ever show one activity.
  // Inherited labels are therefore a good guess, not an observation.
  const videoTask = (hits: { label: string; sim: number }[]): string => {
    if (!hits.length) return '';
    const n = new Map<string, { c: number; best: number }>();
    hits.forEach(({ label, sim }) => {
      const e = n.get(label);
      if (e) {
        e.c += 1;
        e.best = Math.max(e.best, sim);
      } else n.set(label, { c: 1, best: sim });
    });
    return Array.from(n.entries()).sort(
      (a, b) => b[1].c - a[1].c || b[1].best - a[1].best,
    )[0][0];
  };

  const buildManifest = (): string => {
    const rows = ['filename,task,length(min),density'];
    view!.vids.forEach(({ video }) => {
      const hits = labelledHits.get(video.filename) || [];
      // A hand-written summary outranks an inherited activity label: it was
      // written about this video, where the inherited one is a neighbouring
      // clip's guess (measured 81% accurate).
      const fallback = humanLabels?.get(video.filename) || videoTask(hits);
      view!.windows.get(video.filename)!.forEach((w) => {
        const best = hits
          .filter((h) => h.t >= w.start && h.t <= w.end)
          .sort((a, b) => b.sim - a.sim)[0];
        rows.push(
          [
            csvCell(clipName(video.filename, w.start, w.end)),
            csvCell(best ? best.label : fallback),
            ((w.end - w.start + 1) / 60).toFixed(1),
            (w.seenDensity * 100).toFixed(1),
          ].join(','),
        );
      });
    });
    return `${rows.join('\n')}\n`;
  };

  // Everything currently in view (search, categories, night, density all applied),
  // each video clipped to its density windows.
  const downloadAll = () => {
    if (!view || view.vids.length === 0) return;
    const specs = view.vids.map(({ video }) => ({
      filename: video.filename,
      windows: view.windows.get(video.filename)!.map((w) => [w.start, w.end]),
    }));
    downloadClips(specs, buildManifest(), dlFolderName, 'all');
  };

  const openVideo = async (video: WHVideo, hit?: SearchHit) => {
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/wearer-hands/sas?filename=${encodeURIComponent(video.filename)}`,
      );
      if (!resp.ok) throw new Error(`SAS fetch failed (${resp.status})`);
      const { url } = await resp.json();
      const wins = buildWindows(video, densityPct, minClipS, maxGapS);
      // Can be empty when opened from a search hit the density filter excludes.
      const startAt = hit ? Math.max(0, hit.timestamp - 3) : (wins[0]?.start ?? 0);
      const hits = (searchHits || []).filter((h) => h.filename === video.filename);
      setPlayer({ video, url, startAt, hits });
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="wh-page">
      {/* controls */}
      <div className="wh-controls">
        <label>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <option value={24}>24 hours</option>
            <option value={48}>48 hours</option>
            <option value={72}>72 hours</option>
            <option value={0}>All time</option>
          </select>
        </label>
        <form
          className="wh-search"
          onSubmit={(e) => {
            e.preventDefault();
            doSearch(queryText);
          }}
        >
          <input
            type="text"
            placeholder="Search hand activity (e.g. steering wheel)"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
          <button type="submit" disabled={searching}>
            {searching ? '…' : 'Search'}
          </button>
          {activeQuery && (
            <button
              type="button"
              onClick={() => {
                setQueryText('');
                setActiveQuery(null);
                setSearchHits(null);
              }}
            >
              Clear
            </button>
          )}
        </form>
        {/* One menu for both exclusions. Checked = removed, which is the only
            direction that works here: a video with no activity hit (92% of the
            fleet) can be hidden by a task, never selected by one. */}
        <div className="wh-remove">
          <button type="button" onClick={() => setFilterOpen(!filterOpen)}>
            Filter{nRemoved > 0 ? ` (${nRemoved})` : ''} ▾
          </button>
          {filterOpen && (
            <div className="wh-remove-menu">
              {/* Sits above the hint on purpose: this one KEEPS rather than
                  removes, so it must not read as part of the list below it. */}
              <label
                className={`wh-remove-keep${hitsWarmed ? '' : ' wh-check-wait'}`}
                title="Keep only videos with a known task, whether matched by search or written by hand."
              >
                <input
                  type="checkbox"
                  checked={taskOnly}
                  disabled={!hitsWarmed}
                  onChange={(e) => {
                    setTaskOnly(e.target.checked);
                    // Re-read every time it's ticked: a summary written since the
                    // last read has to count, and this is the only moment it matters.
                    if (e.target.checked) loadDescState();
                  }}
                />
                {hitsWarmed ? 'Has a task' : 'Has a task…'}
              </label>
              <div className="wh-remove-divider" />
              <div className="wh-remove-hint">checked items are removed from the view</div>

              <div
                className="wh-remove-section"
                onClick={() => setOpenSection(openSection === 'task' ? null : 'task')}
              >
                <span className="wh-remove-caret">{openSection === 'task' ? '▾' : '▸'}</span>
                Task
                {nCatsRemoved > 0 && <span className="wh-remove-on">{nCatsRemoved}</span>}
                {openSection === 'task' && (
                  <span className="wh-remove-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => { excludeAllCats(); setHideNight(true); }}>
                      all
                    </button>
                    <button type="button" onClick={() => { includeAllCats(); setHideNight(false); }}>
                      none
                    </button>
                  </span>
                )}
              </div>
              {openSection === 'task' && (
                <>
                  <label title="CLIP-detected IR/night-vision videos">
                    <input
                      type="checkbox"
                      checked={hideNight}
                      onChange={(e) => setHideNight(e.target.checked)}
                    />
                    Night / IR 🌙
                  </label>
                  {TOP_ACTIVITIES.map((a) => (
                    <label key={a.label} title={a.queries.join(' · ')}>
                      <input
                        type="checkbox"
                        checked={excludedCats.includes(a.label)}
                        onChange={() => toggleExcluded(a.label)}
                      />
                      {a.label}
                    </label>
                  ))}
                </>
              )}

              <div className="wh-remove-divider" />
              <div
                className="wh-remove-section"
                onClick={() => setOpenSection(openSection === 'industry' ? null : 'industry')}
              >
                <span className="wh-remove-caret">{openSection === 'industry' ? '▾' : '▸'}</span>
                Industry
                {nIndsRemoved > 0 && <span className="wh-remove-on">{nIndsRemoved}</span>}
                {openSection === 'industry' && (
                  <span className="wh-remove-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={excludeAllInds}>
                      all
                    </button>
                    <button type="button" onClick={includeAllInds}>
                      none
                    </button>
                  </span>
                )}
              </div>
              {openSection === 'industry' && (
                <>
                  {industryRows.map(([name, n]) => (
                    <label key={name} title={`${n} videos in this window`}>
                      <input
                        type="checkbox"
                        checked={excludedInds.includes(name)}
                        onChange={() => toggleExcludedInd(name)}
                      />
                      {name} <span className="wh-remove-count">{n}</span>
                    </label>
                  ))}
                  {industryRows.length === 0 && <label>no videos loaded</label>}
                </>
              )}
            </div>
          )}
        </div>
        {/* The three knobs that decide how a clip is CUT, behind one button so
            the toolbar stays readable. Their values stay on the button: they
            silently define every export, and a density left at 75% from an
            earlier session should never be a thing you have to open a menu to
            discover. Same popup behaviour as Filter. */}
        <div className="wh-remove wh-knobs">
          <button type="button" onClick={() => setClipsOpen(!clipsOpen)}>
            Clips ({densityPct}% · {minClipS === 0 ? 'any' : `≥${minClipS}s`} ·{' '}
            {maxGapS === 0 ? 'no gap' : `≤${maxGapS}s`}) ▾
          </button>
          {clipsOpen && (
            <div className="wh-remove-menu wh-knobs-menu">
              <label className="wh-slider">
                <span
                  className="wh-hint"
                  title={
                    'Share of a clip’s sampled seconds that must show hands.'
                  }
                >
                  Sampled density ≥ {densityPct}%
                </span>
                <input
                  type="range"
                  min={0}
                  max={DENSITY_STOPS.length - 1}
                  step={1}
                  value={DENSITY_STOPS.indexOf(densityPct)}
                  onChange={(e) => setDensityPct(DENSITY_STOPS[Number(e.target.value)])}
                />
              </label>
              <label className="wh-slider">
                <span
                  className="wh-hint"
                  title={
                    'Drop clips shorter than this.'
                  }
                >
                  Min length {minClipS === 0 ? 'off' : `≥ ${minClipS}s`}
                </span>
                <input
                  type="range"
                  min={0}
                  max={LENGTH_STOPS.length - 1}
                  step={1}
                  value={LENGTH_STOPS.indexOf(minClipS)}
                  onChange={(e) => setMinClipS(LENGTH_STOPS[Number(e.target.value)])}
                />
              </label>
              <label className="wh-slider">
                <span
                  className="wh-hint"
                  title={
                    'Cut a clip wherever hands go missing for this long.'
                  }
                >
                  Max gap {maxGapS === 0 ? 'none' : `≤ ${maxGapS}s`}
                </span>
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={5}
                  value={maxGapS}
                  onChange={(e) => setMaxGapS(Number(e.target.value))}
                />
              </label>
            </div>
          )}
        </div>
        <button
          type="button"
          className="wh-download-all"
          onClick={() => {
            setDlStage('confirm');
            setDlProgress(null);
            setDlOpen(true);
            loadDescState(); // so the manifest's task column can use them
          }}
          disabled={downloading !== null || !view || view.vids.length === 0}
        >
          {downloading === 'all'
            ? dlProgress && dlProgress.total
              ? `${dlProgress.clips}/${dlProgress.total} clips…`
              : 'Downloading…'
            : '⬇ Download all'}
        </button>
        <button
          type="button"
          className="wh-refresh"
          onClick={doRefresh}
          disabled={loading}
          title="Refresh data"
        >
          <FiRefreshCw className={loading ? 'wh-spin' : ''} />
        </button>
      </div>

      {error && <div className="wh-error">{error}</div>}
      {loading && <div className="wh-loading">Loading data…</div>}

      {view && data && (
        <>
          {/* Just the timestamp. The download estimate and the exclusion counts
              lived here too, but the estimate is already in the Download dialog
              and the counts are a readout of filters you can see set. */}
          <div className="wh-generated">
            generated {new Date(data.generatedAt).toLocaleTimeString()}
          </div>

          {/* stat cards (plix-soc MetricsCard pattern) */}
          <div className="wh-tiles">
            <div className="wh-tile">
              <div className="wh-tile-header">
                <FiClock className="wh-tile-icon" />
                <h3>Hand Time</h3>
              </div>
              <div className="wh-tile-value">{fmtHM(view.totalHandS)}</div>
              <div className="wh-tile-footer">Hand activity inside the kept clips</div>
            </div>
            <div className="wh-tile">
              <div className="wh-tile-header">
                <FiCrosshair className="wh-tile-icon" />
                <h3>Recording Time</h3>
              </div>
              <div className="wh-tile-value">{fmtHM(view.croppedS)}</div>
              <div className="wh-tile-footer">
                Footage in clips at ≥ {densityPct}% sampled-hand density
              </div>
            </div>
            <div className="wh-tile">
              <div className="wh-tile-header">
                <FiVideo className="wh-tile-icon" />
                <h3>Number of Videos</h3>
              </div>
              <div className="wh-tile-value">{view.vids.length}</div>
              <div className="wh-tile-footer">1080p videos at density ≥ {densityPct}%</div>
            </div>
            <div className="wh-tile wh-tile-goal">
              <div className="wh-tile-header">
                <FiTrendingUp className="wh-tile-icon" />
                <h3>Collection Goal</h3>
              </div>
              <div className="wh-goal-stops">
                {GOAL_STOPS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`wh-goal-stop ${g === goalHours ? 'active' : ''}`}
                    onClick={() => setGoalHours(g)}
                  >
                    {g}h
                  </button>
                ))}
              </div>
              <div className="wh-tile-value">
                {!projection ? (
                  '…'
                ) : projection.collected >= goalHours ? (
                  'reached ✓'
                ) : (
                  <>
                    <span className="wh-goal-line">will take </span>
                    {projection.perDay > 0
                      ? `~${Math.ceil((goalHours - projection.collected) / projection.perDay).toLocaleString()} days`
                      : '—'}
                  </>
                )}
              </div>
              <div className="wh-tile-footer">
                {!projection
                  ? 'measuring collection rate…'
                  : projection.collected >= goalHours
                    ? `footage goal met at density ≥ ${densityPct}%`
                    : projection.perDay > 0
                      ? `${(goalHours - projection.collected).toFixed(1)}h of footage to go at avg ${projection.perDay.toFixed(1)} h/day (last ${projection.days} day${projection.days === 1 ? '' : 's'}, density ≥ ${densityPct}%)`
                      : `no qualifying footage in the last ${projection.days || RATE_DAYS} days`}
              </div>
            </div>
            <div className="wh-tile">
              <div className="wh-tile-header">
                <FiUsers className="wh-tile-icon" />
                <h3>Organizations</h3>
              </div>
              <div className="wh-tile-value">{view.orgRows.length}</div>
              <div className="wh-tile-footer">
                {fleetSummary
                  ? `${fleetSummary.pct.toFixed(0)}% of their devices on 1080p`
                  : 'device config stats loading…'}
              </div>
            </div>
          </div>

          {/* an activity/search is filtering the view — obvious way back */}
          {activeQuery && (
            <div className="wh-backbar">
              <button
                type="button"
                className="wh-back"
                onClick={() => {
                  setQueryText('');
                  setActiveQuery(null);
                  setSearchHits(null);
                }}
              >
                ‹ Back to all activities
              </button>
              <span className="wh-backbar-label">
                Showing: <strong>{activeQuery}</strong>
              </span>
            </div>
          )}

          {/* top activities — paged cards, most-common first */}
          {!activeQuery && activityCards.length > 0 && (
            <div className="wh-activities">
              <div className="wh-cards-head">
                <span className="wh-section-label">
                  Top activities (top 3 organizations listed)
                </span>
                {maxCardPage > 0 && (
                  <span className="wh-cards-page">
                    {page + 1} / {maxCardPage + 1}
                  </span>
                )}
              </div>
              <div className="wh-cards-strip">
                <button
                  type="button"
                  className="wh-cards-arrow"
                  onClick={() => setCardPage((page - 1 + maxCardPage + 1) % (maxCardPage + 1))}
                  aria-label="Previous activities"
                >
                  ‹
                </button>
                <div className="wh-cards">
                  {pageCards.map((c) => (
                    <button
                      key={c.activity.label}
                      className="wh-card"
                      title={c.activity.queries.join(' · ')}
                      onClick={() => {
                        setQueryText('');
                        setActiveQuery(c.activity.label);
                        setSearchHits(groupHits(c.activity));
                      }}
                    >
                      {c.preview && (
                        <img
                          className="wh-card-img"
                          loading="lazy"
                          alt=""
                          src={`${API_BASE_URL}/api/wearer-hands/thumb?filename=${encodeURIComponent(c.preview.filename)}&t=${Math.max(0, Math.floor(c.preview.timestamp))}`}
                        />
                      )}
                      <div className="wh-card-label">{c.activity.label}</div>
                      <div className="wh-card-count">
                        {c.count} video{c.count === 1 ? '' : 's'}
                      </div>
                      <div className="wh-card-orgs">
                        {c.orgs.map((o) => (
                          <div key={o} className="wh-card-org">
                            {o}
                          </div>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="wh-cards-arrow"
                  onClick={() => setCardPage((page + 1) % (maxCardPage + 1))}
                  aria-label="Next activities"
                >
                  ›
                </button>
              </div>
            </div>
          )}

          {player && (
            <InlinePlayer
              key={player.video.filename}
              state={player}
              onClose={() => setPlayer(null)}
              onSaved={loadDescState}
            />
          )}

          {/* per-org table: click a row to reveal that org's video cards */}
          <table className="wh-org-table">
            <thead>
              <tr>
                <th>organizations</th>
                <th>hands (minutes)</th>
                <th>footage (hours)</th>
                <th>density @ ≥{densityPct}% (mean/max)</th>
              </tr>
            </thead>
            <tbody>
              {view.orgRows.map(([org, o]) => {
                const d = o.dens.map((x) => x * 100);
                const open = expandedOrgs.has(org);
                const f = orgFleet(o.orgIds);
                return (
                  <React.Fragment key={org}>
                    <tr
                      className={`wh-org-row ${open ? 'open' : ''}`}
                      onClick={() =>
                        setExpandedOrgs((prev) => {
                          const next = new Set(prev);
                          if (next.has(org)) next.delete(org);
                          else next.add(org);
                          return next;
                        })
                      }
                    >
                      <td>
                        <span className="wh-org-caret">{open ? '▾' : '▸'}</span>
                        {org}{' '}
                        <em
                          className="wh-org-count"
                          title={f ? `${f.on}/${f.total} shift devices on the 1080p config` : undefined}
                        >
                          ({o.vids.length}){f ? ` · ${f.pct.toFixed(0)}% on 1080p config` : ''}
                        </em>
                      </td>
                      <td>{(o.handS / 60).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                      <td>{fmtHours(o.croppedS)}</td>
                      <td>
                        {(d.reduce((a, b) => a + b, 0) / d.length).toFixed(1)}/
                        {Math.max(...d).toFixed(1)}
                      </td>
                    </tr>
                    {open && (
                      <tr className="wh-org-expand">
                        <td colSpan={4}>
                          <table className="wh-videos">
                            <thead>
                              <tr>
                                {VID_COLS.map((c) => (
                                  <th
                                    key={c.key}
                                    className={c.num ? 'num' : ''}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setVidSort((prev) =>
                                        prev.key === c.key
                                          ? { key: c.key, desc: !prev.desc }
                                          : { key: c.key, desc: true },
                                      );
                                    }}
                                  >
                                    {c.label}
                                    {vidSort.key === c.key && (
                                      <span className="wh-sort">{vidSort.desc ? '▾' : '▴'}</span>
                                    )}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sortVids(o.vids).map(({ video, hit }) => {
                                const ws = view.windows.get(video.filename) || [];
                                const dens = ws.length
                                  ? (ws.reduce((a, w) => a + w.seenDensity, 0) / ws.length) * 100
                                  : 0;
                                const task = taskOf(video.filename);
                                return (
                                  <tr
                                    key={video.filename}
                                    className={
                                      player?.video.filename === video.filename ? 'active' : ''
                                    }
                                    onClick={() => openVideo(video, hit)}
                                  >
                                    <td>
                                      {recDate(video.filename)}
                                      {isNight(video) ? ' 🌙' : ''}
                                    </td>
                                    <td className="num">{fmtMS(video.duration)}</td>
                                    <td className="num">{fmtMS(video.handSeconds.length)}</td>
                                    <td className="num">{ws.length}</td>
                                    <td className="num">{dens.toFixed(0)}%</td>
                                    <td className={`wh-task wh-task-${task.kind}`} title={task.title}>
                                      {task.text}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {view.vids.length === 0 && (
            <div className="wh-empty">
              {activeQuery && searchHits && searchHits.length > 0
                ? `“${activeQuery}” matched ${searchHits.length} clip${
                    searchHits.length === 1 ? '' : 's'
                  }, but none pass the current filters (1080p, ≥28fps original, selected window).`
                : activeQuery
                  ? `No description matches for “${activeQuery}”.`
                  : 'No videos match the current filters.'}
            </div>
          )}
        </>
      )}

      {dlOpen && dlEstimate && (
        <div
          className="wh-modal-backdrop"
          onClick={() => {
            // Clicking away can't silently kill a download in flight.
            if (dlStage !== 'running') closeDlDialog();
          }}
        >
          <div className="wh-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wh-modal-head">
              <h3>Download all</h3>
              <button type="button" className="wh-close" onClick={closeDlDialog}>
                ✕
              </button>
            </div>

            <table className="wh-modal-table">
              <tbody>
                <tr>
                  <th>Clips</th>
                  <td>
                    {dlEstimate.clips.toLocaleString()} from{' '}
                    {dlEstimate.videos.toLocaleString()} videos
                  </td>
                </tr>
                <tr>
                  <th>Footage</th>
                  <td>{fmtHM(dlEstimate.seconds)}</td>
                </tr>
                <tr>
                  <th>Size</th>
                  <td>~{fmtBytes(dlEstimate.bytes)}</td>
                </tr>
                <tr>
                  <th>Time</th>
                  <td>~{fmtMins(dlEstimate.prepS)}</td>
                </tr>
                <tr>
                  <th>{folderMode ? 'Folder' : 'File'}</th>
                  <td>
                    <span className="wh-modal-mono">
                      {savedDirName ? `${savedDirName}/` : ''}
                      {dlFolderName}
                      {folderMode ? '/' : '.zip'}
                    </span>
                    {folderMode && savedDirName && dlStage === 'confirm' && (
                      <button type="button" className="wh-linkbtn" onClick={forgetDir}>
                        change
                      </button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>


            {dlStage === 'error' && error && (
              <div className="wh-modal-note wh-modal-error">{error}</div>
            )}

            {/* Chrome's blocklist rejects Downloads (and Home) themselves while
                allowing anything nested inside them, and its refusal dialog is a
                dead end — so say what to click before the picker opens. Shown
                only until a folder is remembered. */}
            {dlStage === 'confirm' && folderMode && !savedDirName && (
              <div className="wh-modal-note">
                Chrome won't grant access to Downloads itself. In the picker, hit{' '}
                <strong>New Folder</strong>, name it something like{' '}
                <span className="wh-modal-mono">plixclips</span>, and choose that — you'll
                only do this once.
              </div>
            )}

            {dlStage === 'confirm' &&
              !folderMode &&
              dlEstimate.bytes > BLOB_FALLBACK_WARN_BYTES && (
                <div className="wh-modal-note">
                  This browser can't write to a folder, so the whole{' '}
                  {fmtBytes(dlEstimate.bytes)} zip is held in memory first. Use Chrome, or
                  narrow the selection.
                </div>
              )}

            {dlProgress && (
              <div className="wh-modal-progress">
                <div className="wh-modal-bar">
                  <div
                    className="wh-modal-bar-fill"
                    style={{ width: `${dlProgress.pct}%` }}
                  />
                </div>
                <div className="wh-modal-progress-text">
                  {dlStage === 'running' &&
                    (dlProgress.total !== null ? (
                      <>
                        {dlProgress.clips} of {dlProgress.total} clips ·{' '}
                        {fmtBytes(dlProgress.bytes)}
                        {dlProgress.skipped > 0 && ` · ${dlProgress.skipped} already there`}
                        {dlProgress.etaS !== null ? ` · ~${fmtMins(dlProgress.etaS)} left` : ''}
                      </>
                    ) : (
                      'Cutting and zipping…'
                    ))}
                  {dlStage === 'done' && (
                    <>
                      Done — {dlProgress.total ?? ''}
                      {dlProgress.total ? ' clips, ' : ''}
                      {fmtBytes(dlProgress.bytes)}
                      {dlProgress.skipped > 0 && ` (${dlProgress.skipped} already present)`}
                      {dlProgress.failed > 0 && ` · ${dlProgress.failed} failed to cut`}.
                    </>
                  )}
                  {dlStage === 'cancelled' && (
                    <>
                      Cancelled after {dlProgress.clips ?? 0} clips ({fmtBytes(dlProgress.bytes)}).
                      {dlProgress.total !== null
                        ? ' They are all saved — re-run to pick up where this left off.'
                        : ' The partial zip was discarded.'}
                    </>
                  )}
                  {dlStage === 'error' && <>Stopped — see the error above.</>}
                </div>
              </div>
            )}

            <div className="wh-modal-actions">
              {dlStage === 'running' ? (
                <button type="button" className="wh-modal-cancel" onClick={cancelDownload}>
                  Cancel download
                </button>
              ) : (
                <button type="button" className="wh-download-all" onClick={() => downloadAll()}>
                  {dlStage === 'confirm' ? '⬇ Start download' : '⬇ Download again'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WearerHandsPage;
